<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { SelectInput, TabsMenu } from '@/shared/ui';
  import type { LabViewMode } from '../../model/lab-view-mode';
  import { useMatrixFillLabContext } from '../../model/useMatrixFillLab';
  import SubjectPicker from './SubjectPicker.vue';

  const props = defineProps<{ matrixId: number | null }>();
  const emit = defineEmits<{ (e: 'select-matrix', id: number): void }>();

  const lab = useMatrixFillLabContext();
  const pickerOpen = ref(false);

  function revisionLabel(revision: number | null | undefined): string {
    return revision ? `rev ${revision}` : 'rascunho';
  }

  const matrixOptions = computed(() =>
    lab.matrices.value.map((item) => ({
      id: item.id,
      name: `${item.prefix} ${item.title} (${revisionLabel(item.revision)})`,
    })),
  );

  const VIEW_OPTIONS: { id: LabViewMode; text: string }[] = [
    { id: 'user', text: 'Visão do usuário' },
    { id: 'developer', text: 'Visão do desenvolvedor' },
  ];

  function onSelectMatrix(value: string | number): void {
    const id = Number(value);
    if (id > 0 && id !== props.matrixId) emit('select-matrix', id);
  }
</script>

<template>
  <div
    class="flex flex-col gap-3 rounded-md bg-[var(--color-white-01)] p-3 shadow-sm"
    data-testid="lab-header"
  >
    <div class="flex flex-col gap-1">
      <span class="text-[10px] font-semibold uppercase text-[var(--color-gray-05)]">Matriz</span>
      <SelectInput
        :model-value="matrixId ?? ''"
        :options="matrixOptions"
        label="Selecione uma matriz"
        button-class="bg-white border border-[var(--color-gray-02)] whitespace-nowrap"
        @update:model-value="onSelectMatrix"
      />
    </div>

    <div class="flex flex-col gap-1">
      <div class="flex items-center gap-2">
        <span class="shrink-0 text-[10px] font-semibold uppercase text-[var(--color-gray-05)]">Quem está preenchendo</span>
        <button
          type="button"
          class="ml-auto shrink-0 rounded-md border border-[var(--theme-secondary)] px-2 py-0.5 text-[11px] font-medium text-[var(--theme-secondary)] hover:bg-[var(--theme-secondary)] hover:text-white"
          data-testid="subject-toggle"
          @click="pickerOpen = !pickerOpen"
        >
          {{ pickerOpen ? 'Fechar' : 'Trocar' }}
        </button>
      </div>
      <p
        class="truncate text-sm font-semibold"
        :title="lab.subjectLabel.value"
        data-testid="subject-summary"
      >
        {{ lab.subjectLabel.value }}
      </p>
      <SubjectPicker v-if="pickerOpen" />
    </div>

    <div class="flex justify-center text-xs">
      <TabsMenu
        :options="VIEW_OPTIONS"
        :selected-option="lab.viewMode.value"
        visual-variant="neutral"
        @option-selected="lab.viewMode.value = $event as LabViewMode"
      />
    </div>
  </div>
</template>
